import * as THREE from 'three';
import { Font } from 'three/examples/jsm/loaders/FontLoader.js';
import { Visualizer } from '../visualizer.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort, VDJ_PRESETS } from './lib/vdjLook.js';
import { SceneClock } from './lib/scene-clock.js';

// Contrat du rig partagé : interdit. Le tableau est éclairé par son shader.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'splitflap2',
  family: 'geometric',
  danceability: 0.60,
  requiresTier: 0,
  energyLevel: 0.86,
  bpmMin: 100,
  bpmMax: 175,
  movement: 0.85,
  tonality: 0.40,
  enabled: true
};

// SPLITFLAP2 : un immense tableau à palettes (gares, aéroports) qui remplit
// l'écran, 64 x 36 modules (48 x 27 au tier 0). Chaque module bascule avec
// le claquement caractéristique : la demi-palette supérieure tombe, dévoile
// la carte suivante, et 3 à 6 cartes intermédiaires défilent (80 ms
// chacune) avant la cible, comme sur un vrai tableau. Ensemble ils forment
// des IMAGES : la pochette en pastilles, le titre en lettres géantes, des
// motifs, l'heure. LOOK VDJ party (lib/vdjLook.js), dansabilité 0,60 : gamme
// R9 « 0,55 à 0,80 », contenus coupés sur la demi-phrase, vague de temps
// modérée, aucun flash. House, techno, électro, pop, hip-hop. (Le registre a déjà un `splitflap` : les deux sont gardés.)
//
// LES CARTES : un espace d'index unique de 128 cartes, les 24 premières des
// COULEURS (la palette de la pochette réduite à 24 teintes, R4), puis les
// GLYPHES (A-Z, 0-9, ponctuation), blancs sur noir. Les glyphes sont de la
// GÉOMÉTRIE (le typeface.json d'Anton de Typeclash, Font.generateShapes,
// ShapeGeometry), rendue UNE fois à l'init dans un atlas de 128 px par
// cellule que le module rééchantillonne : net à la taille d'un module,
// sans pixel (R21 du brief).
//
// L'ÉTAT SUR GPU : une texture flottante 64 x 36 en ping-pong (R carte
// courante, G carte suivante, B bascules restantes + angle, A dernier
// départ pris en compte), avancée chaque image par une passe de
// simulation à partir d'une texture CIBLE (R carte cible, G instant de
// départ, B nombre d'intermédiaires, A départ de la vague de temps), écrite
// par le CPU seulement quand le contenu change (une fois par mesure, ou par
// croche en montée). Quand le départ d'un module arrive, il charge
// K intermédiaires et bascule carte par carte jusqu'à la cible ; la vague
// de temps est deux bascules (carte + 1 puis retour).
//
// LE RENDU : 2 304 quads instanciés, un par module ; le fragment dessine le
// boîtier noir mat, la fente, la demi-palette haute (la carte suivante si une
// bascule est en cours, déjà dévoilée), la demi-palette basse (la carte
// courante), et la PALETTE QUI TOMBE : une rotation autour de l'axe central,
// hauteur apparente cos(theta), face avant (haut de la carte courante) puis
// dos (bas de la carte suivante), assombrie par l'inclinaison, un reflet
// spéculaire qui glisse, une ombre portée sur la demi-palette basse, un flou
// de mouvement léger près de l'horizontale. Les cartes ont un léger bombé.
//
// LA CASCADE : le changement de contenu ne bascule jamais tout en même temps :
// un FRONT traverse le tableau (gauche-droite, depuis le centre, diagonale,
// spirale, pluie), le délai de chaque module calé sur les DOUBLES-CROCHES.
// Rythme (R9, 0,60) : temps = une vague vide sur une rangée ; toutes les
// DEUX mesures = nouveau contenu et nouveau front sur une mesure entière ;
// phrase = famille de contenus et palette ; montée = fronts sur les
// doubles-croches resserrés, le tableau s'affole seulement dans les quatre
// dernières mesures ; drop = tout bascule ensemble sur la pochette, SANS
// flash. Scène de 20 s (R20) : la pochette formée dans les 2
// premières secondes, le titre vers 8 s.
//
// Repli sans pochette : palette de secours et motifs ; sans BPM : horloge
// libre à 128 ; sans titre : l'heure et le BPM ; en pause : tout gèle.
// Debug : ?flapcontent=cover|title|pattern|clock|text, ?flapfront=lr|center|
// diag|spiral|rain, ?flapprobe=1 (window.__splitflap2).
//
// precision highp (exception documentée) : les index de cartes et les
// instants dans la texture d'état.

const FONT_FILE = 'fonts/typeclash-condensed.typeface.json';
const COLS = 64, ROWS = 36;
const TIER = {
  0: { cols: 48, rows: 27, inter: 1 },
  1: { cols: 64, rows: 36, inter: 4 },
  2: { cols: 64, rows: 36, inter: 5 }
};
const N_COLORS = 24;
const GLYPHS = ' ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789:.-\'&/!?';
const N_CARDS = 128;                               // the index space
const N_USED = N_COLORS + GLYPHS.length;           // the cards that exist on the drum
const ATLAS_GRID = 8, ATLAS_CELL = 128;          // 64 glyph cells of 128 px
const FLIP_S = 0.08;
const CYCLE = 20;
const NO_BPM = 128;
const SAFE_FLOOR = 0.5;
const FRONTS = ['lr', 'center', 'diag', 'spiral', 'rain'];
const CONTENTS = ['cover', 'title', 'pattern', 'clock', 'text'];
// a 3 x 5 pixel font for the giant letters
export const FONT3x5 = {   // exported for dancefloor.js (letters made of tiles)
  A: '010101111101101', B: '110101110101110', C: '011100100100011', D: '110101101101110', E: '111100110100111', F: '111100110100100', G: '011100101101011', H: '101101111101101',
  I: '111010010010111', J: '001001001101010', K: '101101110101101', L: '100100100100111', M: '101111111101101', N: '110101101101101', O: '010101101101010', P: '110101110100100',
  Q: '010101101011001', R: '110101110101101', S: '011100010001110', T: '111010010010010', U: '101101101101111', V: '101101101101010', W: '101101111111101', X: '101101010101101',
  Y: '101101010010010', Z: '111001010100111', '0': '111101101101111', '1': '010110010010111', '2': '110001010100111', '3': '110001010001110', '4': '101101111001001', '5': '111100110001110',
  '6': '011100110101010', '7': '111001010010010', '8': '010101010101010', '9': '010101011001110', ':': '000010000010000', '-': '000000111000000', '.': '000000000000010', ' ': '000000000000000'
};

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}
function mulberry(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

let _fontPromise = null;
function loadFont() {
  if (_fontPromise) return _fontPromise;
  _fontPromise = fetch(FONT_FILE).then((r) => { if (!r.ok) throw new Error(FONT_FILE + ' HTTP ' + r.status); return r.json(); }).then((j) => new Font(j)).catch((e) => { console.warn('[splitflap2] font missing', e); return null; });
  return _fontPromise;
}

// --- shaders --------------------------------------------------------------------------

const quadVertex = `
  precision highp float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
// the simulation: state (R current, G next, B remaining + angle, A last content start handled) <- target (R card, G start, B intermediates, A wave start)
// the wave is taken in a window shorter than its two flips, so it fires once and never blocks a content front
const simFragment = `
  precision highp float;
  uniform sampler2D uState, uTarget;
  uniform float uTime, uDt, uFlip, uMad, uCards;    // uMad: the build - keep flipping; uCards: cards on the drum
  varying vec2 vUv;
  // the drum has two ranges of cards: the colours (0..23) and the glyphs (24..uCards-1); a flip toward a
  // target steps within the target's range, so a colour never shows glyph cards on its way (and vice versa)
  float rangeLo(float c) { return c < ${N_COLORS}.0 - 0.5 ? 0.0 : ${N_COLORS}.0; }
  float rangeN(float c) { return c < ${N_COLORS}.0 - 0.5 ? ${N_COLORS}.0 : uCards - ${N_COLORS}.0; }
  float stepUp(float c, float ref) { float lo = rangeLo(ref), n = rangeN(ref); return lo + mod(c - lo + 1.0 + n, n); }
  float stepDown(float c, float ref) { float lo = rangeLo(ref), n = rangeN(ref); return lo + mod(c - lo - 1.0 + n, n); }
  void main() {
    vec4 s = texture2D(uState, vUv), t = texture2D(uTarget, vUv);
    float cur = s.r, nxt = s.g, rem = floor(s.b), ang = fract(s.b), handled = s.a;
    float target = t.r, start = t.g, inter = t.b, wave = t.a;
    if (rem > 0.5) {
      ang += uDt / uFlip;
      if (ang >= 1.0) {
        ang = 0.0; cur = nxt;
        if (rem > 101.5) { rem = 101.0; nxt = stepDown(cur, cur); }   // the wave: back to the card it left
        else if (rem > 99.5) { rem = 0.0; nxt = cur; }
        else {
          rem -= 1.0;
          if (rem > 0.5) nxt = rem > 1.5 ? stepUp(cur, target) : target;
          else nxt = cur;
        }
      }
    } else {
      // a new content front reached this module
      if (uTime >= start && start > handled && abs(cur - target) > 0.5) {
        handled = start;
        rem = inter + 1.0;
        nxt = inter > 0.5 ? rangeLo(target) + mod(target - rangeLo(target) - inter + rangeN(target), rangeN(target)) : target;
        ang = 0.0;
      } else if (uTime >= wave && uTime < wave + 0.1 && wave > 0.0) {
        // the beat's empty wave: one card up, then back (two flips, 0.16 s > the window)
        rem = 102.0; nxt = stepUp(cur, cur); ang = 0.0;
      } else if (uMad > 0.5 && uTime >= start && abs(cur - target) < 0.5 && mod(uTime + vUv.x * 7.0 + vUv.y * 13.0, 0.5) < uDt) {
        // the build: the board goes mad, flipping on without stopping
        rem = 2.0; nxt = stepUp(cur, cur); ang = 0.0;
      } else if (uTime >= start && start > handled) handled = start;   // already on target: nothing to do
    }
    gl_FragColor = vec4(cur, nxt, rem + ang, handled);
  }
`;
const moduleVertex = `
  precision highp float;
  attribute vec2 aCell;         // column, row
  uniform vec2 uGrid;           // cols, rows
  uniform float uAspect;
  varying vec2 vUv, vCell;
  void main() {
    vUv = uv; vCell = aCell;
    // the board fills the frame: modules of width 2 aspect / cols and height 2 / rows (clip units)
    vec2 size = vec2(2.0 / uGrid.x, 2.0 / uGrid.y);
    vec2 p = vec2(-1.0, -1.0) + (aCell + uv) * size;
    gl_Position = vec4(p, 0.0, 1.0);
  }
`;
const moduleFragment = `
  precision highp float;
  uniform sampler2D uState, uAtlas;
  uniform vec2 uGrid, uScreen;
  uniform float uAspect, uGain, uFlash, uFlip, uGlyphScale;
  uniform vec3 uPalette[${N_COLORS}];
  uniform vec2 uSafeCenter, uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec2 vUv, vCell;
  float safeAtt() {
    vec2 s = (gl_FragCoord.xy / uScreen * 2.0 - 1.0) * vec2(uAspect, 1.0);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  vec3 paletteColor(float idx) {
    vec3 c = uPalette[0];
    for (int i = 1; i < ${N_COLORS}; i++) if (abs(idx - float(i)) < 0.5) c = uPalette[i];
    return c;
  }
  // a card's face at local uv (0..1 across the full card), with the bulge shading
  vec3 card(float idx, vec2 cu) {
    vec3 c;
    if (idx < ${N_COLORS}.0 - 0.5) c = paletteColor(idx);
    else {
      float g = idx - ${N_COLORS}.0;
      vec2 cell = vec2(mod(g, ${ATLAS_GRID}.0), floor(g / ${ATLAS_GRID}.0));
      vec2 guv = (cu - 0.5) * uGlyphScale + 0.5;
      float a = (guv.x < 0.0 || guv.x > 1.0 || guv.y < 0.0 || guv.y > 1.0) ? 0.0 : texture2D(uAtlas, (cell + guv) / ${ATLAS_GRID}.0).r;
      c = mix(vec3(0.04), vec3(0.92), a);
    }
    // the bulge: a little lighter toward the top of each half, a touch darker at the edges
    float half_ = cu.y > 0.5 ? (cu.y - 0.5) * 2.0 : cu.y * 2.0;
    c *= 0.86 + 0.14 * half_ - 0.10 * pow(abs(cu.x - 0.5) * 2.0, 4.0);
    return c;
  }
  void main() {
    vec2 su = (vCell + 0.5) / uGrid;
    vec4 s = texture2D(uState, su);
    float cur = s.r, nxt = s.g, rem = floor(s.b), ang = fract(s.b);
    bool flipping = rem > 0.5;
    // the module: housing margins, the card inside
    vec2 m = vec2(0.055, 0.07);
    vec2 cu = (vUv - m) / (1.0 - 2.0 * m);                 // card uv
    float inCard = step(0.0, cu.x) * step(cu.x, 1.0) * step(0.0, cu.y) * step(cu.y, 1.0);
    float px = 1.0 / (uScreen.y / uGrid.y);                 // one screen pixel in module uv
    vec3 housing = vec3(0.025) * (0.8 + 0.2 * vUv.y);
    vec3 c = housing;
    if (inCard > 0.5) {
      // the static halves: top shows the next card while a flip reveals it, bottom the current card
      vec3 topCard = card(flipping ? nxt : cur, cu);
      vec3 botCard = card(cur, cu);
      c = cu.y > 0.5 ? topCard : botCard;
      if (flipping) {
        float theta = ang * 3.14159265;
        float h = cos(theta);                              // apparent half-height of the falling flap, signed
        float speed = sin(theta);                          // fastest at the horizontal: motion blur, shadow strongest
        if (h > 0.0) {
          // falling over the top half: the front face (the current card's top), compressed
          float yTop = 0.5 + 0.5 * h;
          if (cu.y > 0.5 && cu.y < yTop) {
            vec2 fu = vec2(cu.x, 0.5 + (cu.y - 0.5) / max(h, 1e-3) * 1.0);
            vec3 face = card(cur, fu) * (0.55 + 0.45 * h);
            float spec = exp(-pow((fu.y - (0.55 + 0.4 * ang * 2.0)) / 0.06, 2.0)) * 0.35 * speed;
            face += vec3(spec);
            c = mix(c, face, 1.0 - 0.5 * speed * speed);    // the blur: the face thins near the horizontal
          }
        } else {
          // past the horizontal: the back face (the next card's bottom) covering the bottom half from the centre down
          float yBot = 0.5 + 0.5 * h;                      // h < 0
          if (cu.y < 0.5 && cu.y > yBot) {
            vec2 fu = vec2(cu.x, 0.5 - (0.5 - cu.y) / max(-h, 1e-3) * 0.5);
            vec3 face = card(nxt, fu) * (0.55 + 0.45 * (-h));
            float spec = exp(-pow((fu.y - (0.45 - 0.4 * (ang - 0.5) * 2.0)) / 0.06, 2.0)) * 0.3 * speed;
            face += vec3(spec);
            c = mix(c, face, 1.0 - 0.5 * speed * speed);
          }
          // the drop shadow of the flap on the bottom half, just under its edge
          float sh = exp(-max(yBot - cu.y, 0.0) / 0.08) * step(cu.y, yBot) * 0.55 * speed;
          c *= 1.0 - sh;
        }
        // the shadow under the top flap on the bottom half near the slot
        if (h > 0.0 && cu.y < 0.5) c *= 1.0 - 0.35 * speed * exp(-(0.5 - cu.y) / 0.06);
      }
      // the slot: a dark line at the middle
      c *= 1.0 - 0.85 * (1.0 - smoothstep(0.0, 1.6 * px, abs(cu.y - 0.5) - 0.006));
      // the card's rounded edge: darker rim
      vec2 e = min(cu, 1.0 - cu);
      c *= 0.7 + 0.3 * smoothstep(0.0, 0.05, min(e.x, e.y));
    }
    c = mix(c, vec3(1.0), uFlash);
    gl_FragColor = vec4(c * safeAtt() * uGain, 1.0);
  }
`;
const glyphVertex = `
  precision highp float;
  uniform vec4 uPlace;          // x, y, w, h of the cell in clip space
  void main() { gl_Position = vec4(uPlace.xy + position.xy * uPlace.zw, 0.0, 1.0); }
`;
const glyphFragment = `
  precision highp float;
  void main() { gl_FragColor = vec4(1.0); }
`;

export class Splitflap2Visualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null && TIER[o.qualityTier] ? o.qualityTier : 2;
    this._layout = TIER[this._tier];
    this._cols = this._layout.cols; this._rows = this._layout.rows;
    const fc = urlParam('flapcontent'); this._forcedContent = CONTENTS.includes(fc) ? fc : null;
    const ff = urlParam('flapfront'); this._forcedFront = FRONTS.includes(ff) ? ff : null;
    this._post = new VdjPost({ bloomStrength: 0.12, bloomThreshold: 0.78, grain: 0.015, vignette: 0.3 });
    this._comfort = new VdjComfort(VDJ_PRESETS.party.comfort);
    this._clock = new SceneClock({ period: CYCLE, start: 0 });
    this._rng = mulberry(0x5F3759DF ^ Math.floor((this._clock.elapsed + 1) * 331));
    this._decisions = [];
    if (urlParam('flapprobe') === '1' && typeof window !== 'undefined') window.__splitflap2 = this;
  }

  get postProcessing() { return this._post.value; }

  init(scene) {
    this._scene = scene;
    const C = this._cols, R = this._rows;
    this._time = 0; this._beats = 0; this._beatCount = 0; this._barCount = 0; this._phraseCount = 0; this._beatPeriod = 60 / NO_BPM; this._free = 0;
    this._lastBeatPhase = null; this._lastBarPhase = null; this._lastPhrasePhase = null; this._lastBeatAt = -10; this._lastBarAt = -10;
    this._build = 0; this._lastDropKey = null; this._dropAt = -10; this._flash = 0; this._waveRow = 0; this._lumSince = 0;
    this._content = 'cover'; this._front = 'center'; this._family = 0; this._pal = 0; this._titleDone = false;
    this._coverUrl = undefined; this._cover = null; this._coverPending = null; this._coverGrid = null; this._palette = null;
    this._title = ''; this._artist = ''; this._font = null; this._atlasReady = false; this._bands = [0.5, 0.4, 0.4];
    this._size = new THREE.Vector2(); this._aspect = 0;
    const safe = { uSafeCenter: safeZoneUniforms.uSafeCenter, uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather };
    // the state (ping-pong) and the target
    const rt = () => new THREE.WebGLRenderTarget(C, R, { type: THREE.FloatType, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter, depthBuffer: false, stencilBuffer: false, generateMipmaps: false });
    this._stateA = rt(); this._stateB = rt();
    this._targetData = new Float32Array(C * R * 4);
    this._targetTex = new THREE.DataTexture(this._targetData, C, R, THREE.RGBAFormat, THREE.FloatType);
    this._targetTex.minFilter = this._targetTex.magFilter = THREE.NearestFilter; this._targetTex.needsUpdate = true;
    this._simU = { uState: { value: this._stateA.texture }, uTarget: { value: this._targetTex }, uTime: { value: 0 }, uDt: { value: 0 }, uFlip: { value: FLIP_S }, uMad: { value: 0 }, uCards: { value: N_USED } };
    this._simMat = new THREE.ShaderMaterial({ uniforms: this._simU, vertexShader: quadVertex, fragmentShader: simFragment, depthTest: false, depthWrite: false });
    this._passScene = new THREE.Scene(); this._passQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), this._simMat); this._passQuad.frustumCulled = false; this._passScene.add(this._passQuad);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10); this._ortho.position.z = 1;
    // the glyph atlas (vector glyphs rendered once)
    this._atlas = new THREE.WebGLRenderTarget(ATLAS_GRID * ATLAS_CELL, ATLAS_GRID * ATLAS_CELL, { type: THREE.UnsignedByteType, minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false, generateMipmaps: true, samples: 4 });
    this._glyphMat = new THREE.ShaderMaterial({ uniforms: { uPlace: { value: new THREE.Vector4() } }, vertexShader: glyphVertex, fragmentShader: glyphFragment, depthTest: false, depthWrite: false, side: THREE.DoubleSide });
    this._glyphScene = new THREE.Scene();
    // the modules
    const geo = new THREE.InstancedBufferGeometry();
    const quad = new THREE.PlaneGeometry(1, 1); quad.translate(0.5, 0.5, 0);
    geo.index = quad.index; geo.setAttribute('position', quad.getAttribute('position')); geo.setAttribute('uv', quad.getAttribute('uv'));
    const cells = new Float32Array(C * R * 2);
    for (let y = 0; y < R; y++) for (let x = 0; x < C; x++) { cells[(y * C + x) * 2] = x; cells[(y * C + x) * 2 + 1] = y; }
    geo.setAttribute('aCell', new THREE.InstancedBufferAttribute(cells, 2));
    geo.instanceCount = C * R; geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e4);
    this._palU = Array.from({ length: N_COLORS }, () => new THREE.Color(0.2, 0.2, 0.2));
    this._u = Object.assign({
      uState: { value: this._stateA.texture }, uAtlas: { value: this._atlas.texture }, uGrid: { value: new THREE.Vector2(C, R) }, uScreen: { value: new THREE.Vector2(1280, 720) },
      uAspect: { value: 16 / 9 }, uGain: { value: 1 }, uFlash: { value: 0 }, uFlip: { value: FLIP_S }, uGlyphScale: { value: 1.0 }, uPalette: { value: this._palU }
    }, safe);
    this._mat = new THREE.ShaderMaterial({ uniforms: this._u, vertexShader: moduleVertex, fragmentShader: moduleFragment, depthTest: false, depthWrite: false });
    this._mesh = new THREE.Mesh(geo, this._mat); this._mesh.frustumCulled = false; this._mesh.renderOrder = -1000;
    const self = this;
    this._mesh.onBeforeRender = function (renderer) { self._render(renderer); };
    scene.add(this._mesh);
    this._fallbackPalette();
    this._stateInit = true;
    this._setTarget('cover', 'center', 0.2, 1.6, false);
    loadFont().then((f) => { this._font = f; });
  }

  _say(what) { this._decisions.push({ t: +this._time.toFixed(2), bar: this._barCount, what }); if (this._decisions.length > 48) this._decisions.shift(); }

  // --- the palette and the cover grid ----------------------------------------------------

  _fallbackPalette() {
    // 24 tones: three VDJ hues in eight steps each, dark to light
    const hues = [0xff2bd6, 0x2bffea, 0xffc21a];
    this._palette = [];
    for (let i = 0; i < N_COLORS; i++) { const c = vdjColor(hues[i % 3]); const k = 0.15 + 0.85 * (Math.floor(i / 3) / 7); c.multiplyScalar(k); this._palette.push(c); }
    for (let i = 0; i < N_COLORS; i++) this._palU[i].copy(this._palette[i]);
  }
  _loadCover(url) {
    this._coverUrl = url;
    if (!url) { this._dropCover(); return; }
    const token = {};
    this._coverPending = token;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: false }, (texture) => {
      if (this._coverPending !== token) { texture.dispose(); return; }
      this._coverPending = null;
      if (this._cover) this._cover.dispose();
      this._cover = texture;
      this._analyseCover(texture.image);
    }, () => { if (this._coverPending === token) { this._coverPending = null; this._dropCover(); } });
  }
  _dropCover() { if (this._cover) this._cover.dispose(); this._cover = null; this._coverGrid = null; this._fallbackPalette(); }
  // the cover covering the board (R18), one mean colour per module, quantised to 24 colours (k-means on the module means)
  _analyseCover(img) {
    if (typeof document === 'undefined' || !img) return;
    const C = this._cols, R = this._rows;
    try {
      const cv = document.createElement('canvas'); cv.width = C; cv.height = R;
      const ctx = cv.getContext('2d', { willReadFrequently: true });
      const iw = img.width || img.videoWidth || 1, ih = img.height || img.videoHeight || 1;
      const scale = Math.max(C / iw, R / ih), dw = iw * scale, dh = ih * scale;
      ctx.drawImage(img, (C - dw) / 2, (R - dh) / 2, dw, dh);
      const d = ctx.getImageData(0, 0, C, R).data;
      const pts = [];
      for (let i = 0; i < C * R; i++) pts.push([Math.pow(d[i * 4] / 255, 2.2), Math.pow(d[i * 4 + 1] / 255, 2.2), Math.pow(d[i * 4 + 2] / 255, 2.2)]);
      // k-means, 24 centres seeded on a luminance-sorted spread
      const sorted = pts.slice().sort((a, b) => (a[0] + a[1] + a[2]) - (b[0] + b[1] + b[2]));
      let centres = Array.from({ length: N_COLORS }, (_, k) => sorted[Math.floor((k + 0.5) / N_COLORS * sorted.length)].slice());
      const assign = new Int32Array(pts.length);
      for (let it = 0; it < 8; it++) {
        const sum = centres.map(() => [0, 0, 0, 0]);
        for (let i = 0; i < pts.length; i++) {
          let best = 0, bd = Infinity;
          for (let k = 0; k < N_COLORS; k++) { const c = centres[k]; const dd = (pts[i][0] - c[0]) ** 2 + (pts[i][1] - c[1]) ** 2 + (pts[i][2] - c[2]) ** 2; if (dd < bd) { bd = dd; best = k; } }
          assign[i] = best; sum[best][0] += pts[i][0]; sum[best][1] += pts[i][1]; sum[best][2] += pts[i][2]; sum[best][3]++;
        }
        centres = sum.map((s, k) => s[3] ? [s[0] / s[3], s[1] / s[3], s[2] / s[3]] : centres[k]);
      }
      // the printed-dot look: saturation pushed a little, never below a floor of light
      this._palette = centres.map((c) => { const col = new THREE.Color(c[0], c[1], c[2]); const h = {}; col.getHSL(h); col.setHSL(h.h, Math.min(1, h.s * 1.25 + 0.05), Math.max(0.10, Math.min(0.72, h.l))); return col; });
      for (let i = 0; i < N_COLORS; i++) this._palU[i].copy(this._palette[i]);
      // the board's row 0 is the bottom: flip the canvas rows
      this._coverGrid = new Array(C * R);
      for (let y = 0; y < R; y++) for (let x = 0; x < C; x++) this._coverGrid[(R - 1 - y) * C + x] = assign[y * C + x];
      if (this._content === 'cover') this._setTarget('cover', this._front, this._time + 0.05, 1.2, false);
    } catch (e) { this._coverGrid = null; }
  }

  // --- the contents: a 64 x 36 grid of card indices -----------------------------------------

  _gridCover() {
    const C = this._cols, R = this._rows, g = new Float32Array(C * R);
    if (this._coverGrid) { for (let i = 0; i < C * R; i++) g[i] = this._coverGrid[i]; return g; }
    // no cover: a soft gradient of the fallback palette
    for (let y = 0; y < R; y++) for (let x = 0; x < C; x++) g[y * C + x] = Math.min(N_COLORS - 1, Math.floor((x / C * 0.5 + y / R * 0.5) * N_COLORS));
    return g;
  }
  _glyphIndex(ch) { const i = GLYPHS.indexOf(ch); return N_COLORS + (i < 0 ? GLYPHS.indexOf('?') : i); }
  _dark() { let best = 0, bl = Infinity; for (let i = 0; i < N_COLORS; i++) { const c = this._palette[i]; const l = c.r * 0.2126 + c.g * 0.7152 + c.b * 0.0722; if (l < bl) { bl = l; best = i; } } return best; }
  _light() { let best = 0, bl = -1; for (let i = 0; i < N_COLORS; i++) { const c = this._palette[i]; const l = c.r * 0.2126 + c.g * 0.7152 + c.b * 0.0722; if (l > bl) { bl = l; best = i; } } return best; }
  // giant letters: 3 x 5 modules each, centred, wrapped to the width; the title in the light tone on the dark one
  _gridBig(lines) {
    const C = this._cols, R = this._rows, g = new Float32Array(C * R);
    const bg = this._dark(), fg = this._light();
    g.fill(bg);
    const scale = R >= 36 ? 1 : 1;
    const lw = 4 * scale, lh = 6 * scale;
    const totalH = lines.length * lh - 1;
    let y0 = Math.floor((R - totalH) / 2);
    for (const line of lines) {
      const w = line.length * lw - 1;
      let x0 = Math.floor((C - w) / 2);
      for (const ch of line) {
        const bits = FONT3x5[ch] || FONT3x5[' '];
        for (let r = 0; r < 5; r++) for (let c = 0; c < 3; c++) if (bits[r * 3 + c] === '1') {
          const x = x0 + c, y = y0 + r;
          if (x >= 0 && x < C && y >= 0 && y < R) g[(R - 1 - y) * C + x] = fg;   // row 0 of the grid is the bottom
        }
        x0 += lw;
      }
      y0 += lh;
    }
    return g;
  }
  // small text: one glyph per module, centred lines, the rest a dark card
  _gridText(lines) {
    const C = this._cols, R = this._rows, g = new Float32Array(C * R);
    g.fill(this._glyphIndex(' '));
    const y0 = Math.floor((R - (lines.length * 3 - 2)) / 2);
    lines.forEach((line, li) => {
      const text = line.slice(0, C - 4);
      const x0 = Math.floor((C - text.length) / 2), y = R - 1 - (y0 + li * 3);
      for (let i = 0; i < text.length; i++) if (y >= 0 && y < R) g[y * C + x0 + i] = this._glyphIndex(text[i]);
    });
    return g;
  }
  _gridPattern(kind) {
    const C = this._cols, R = this._rows, g = new Float32Array(C * R);
    const a = this._dark(), b = this._light(), c2 = (this._pal * 7 + 3) % N_COLORS, c3 = (this._pal * 11 + 9) % N_COLORS;
    for (let y = 0; y < R; y++) for (let x = 0; x < C; x++) {
      let v;
      if (kind === 0) v = ((x >> 2) + (y >> 2)) % 2 ? a : b;                                   // checker
      else if (kind === 1) v = [a, b, c2, c3][((x + y) >> 2) % 4];                              // diagonal stripes
      else if (kind === 2) { const d = Math.hypot((x - C / 2) / (C / 2) * 1.78, (y - R / 2) / (R / 2)); v = [a, b, c2, c3][Math.floor(d * 6) % 4]; }   // circles
      else { const t = x / C; const band = t < 0.5 ? this._bands[0] * (1 - t * 2) + this._bands[1] * t * 2 : this._bands[1] * (2 - t * 2) + this._bands[2] * (t * 2 - 1); const h = 0.15 + 0.8 * band * (0.7 + 0.3 * Math.sin(x * 0.9 + this._barCount)); v = (y / R) < h ? [b, c2, c3][Math.floor((y / R) / h * 3) % 3] : a; }   // spectrum wave
      g[y * C + x] = v;
    }
    return g;
  }
  _gridClock(bpm) {
    const d = new Date();
    const hh = String(d.getHours()).padStart(2, '0'), mm = String(d.getMinutes()).padStart(2, '0');
    return this._gridBig([hh + ':' + mm, Math.round(bpm) + ' BPM'.slice(0, 4)]);
  }

  // --- the fronts: a start time per module, on the sixteenths -------------------------------------
  _frontDelays(kind, stepS, maxSteps) {
    const C = this._cols, R = this._rows, out = new Float32Array(C * R);
    const rng = this._rng;
    for (let y = 0; y < R; y++) for (let x = 0; x < C; x++) {
      const u = x / (C - 1), v = y / (R - 1);
      let f;
      if (kind === 'lr') f = u;
      else if (kind === 'center') f = Math.hypot((u - 0.5) * 1.78, v - 0.5) / 1.03;
      else if (kind === 'diag') f = (u + v) / 2;
      else if (kind === 'spiral') { const dx = (u - 0.5) * 1.78, dy = v - 0.5; const r = Math.hypot(dx, dy) / 1.03, a = (Math.atan2(dy, dx) / Math.PI + 1) / 2; f = (r * 0.7 + a * 0.3); }
      else f = rng();
      out[y * C + x] = Math.round(clamp01(f) * maxSteps) * stepS;
    }
    return out;
  }

  // write the target texture: cards, start times, intermediates
  _setTarget(content, front, startAt, durationS, mad) {
    const C = this._cols, R = this._rows, T = this._targetData;
    let grid;
    const bpm = 60 / this._beatPeriod;
    if (content === 'cover') grid = this._gridCover();
    else if (content === 'title') grid = this._gridBig(this._titleLines());
    else if (content === 'text') { const d = new Date(); grid = this._gridText([(this._title || 'NOW PLAYING').toUpperCase(), (this._artist || '').toUpperCase(), String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0') + '   ' + Math.round(bpm) + ' BPM'].filter(Boolean)); }
    else if (content === 'clock') grid = this._gridClock(bpm);
    else grid = this._gridPattern(this._barCount % 4);
    const step = this._beatPeriod / (mad ? 8 : 4);                       // sixteenths; thirty-seconds in the build
    const maxSteps = Math.max(1, Math.round(durationS / step));
    const delays = this._frontDelays(front, step, maxSteps);
    const inter = this._layout.inter;
    for (let i = 0; i < C * R; i++) {
      T[i * 4] = grid[i];
      T[i * 4 + 1] = startAt + delays[i];
      T[i * 4 + 2] = inter;
      // the wave channel keeps its last value (set per row on beats)
    }
    this._targetTex.needsUpdate = true;
    this._content = content; this._front = front;
    this._lastGrid = grid;
    this._holdUntil = startAt + durationS + (content === 'title' || content === 'clock' || content === 'text' ? this._beatPeriod * 8 : this._beatPeriod * 2);
    this._say(content + ' / ' + front + (mad ? ' (mad)' : ''));
  }
  _titleLines() {
    const words = (this._title || '').toUpperCase().replace(/[^A-Z0-9 :.\-]/g, '').split(/\s+/).filter(Boolean);
    const maxChars = Math.floor((this._cols + 1) / 4);
    const lines = []; let line = '';
    for (const w of words) { const t = line ? line + ' ' + w : w; if (t.length > maxChars) { if (line) lines.push(line); line = w.slice(0, maxChars); } else line = t; }
    if (line) lines.push(line);
    if (!lines.length) lines.push('PLAY');
    return lines.slice(0, Math.floor(this._rows / 6));
  }
  _wave(row) {
    const C = this._cols, T = this._targetData, now = this._time + 0.01;
    for (let x = 0; x < C; x++) T[(row * C + x) * 4 + 3] = now + x * 0.012;   // the empty wave runs along the row
    this._targetTex.needsUpdate = true;
  }

  // --- per frame ---------------------------------------------------------------------

  update(deltaTime, profile) {
    const paused = profile.playbackState !== undefined && profile.playbackState !== 'PLAYING';
    const dt = paused ? 0 : Math.min(deltaTime, 0.05);
    this._clock.update(dt);
    if ((profile.artworkUrl || null) !== this._coverUrl) this._loadCover(profile.artworkUrl || null);
    const title = String(profile.title || '').trim(), artist = String(profile.artist || '').trim();
    if (title !== this._title || artist !== this._artist) { this._title = title; this._artist = artist; }
    this._dt = dt;
    if (paused) return;
    this._time += dt;
    const director = profile.director || {};
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || NO_BPM;
    this._beatPeriod = 60 / THREE.MathUtils.clamp(bpm, 60, 200);
    const bands = director.hasReliableGrid !== false && director.bands ? director.bands : null;
    if (bands) this._bands = [clamp01(bands.low || 0), clamp01(bands.mid || 0), clamp01(bands.high || 0)];

    const beatPhase = director.beat != null ? director.beat : profile.beatPhase;
    let beatEdge = false;
    if (beatPhase != null) { if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) beatEdge = true; this._lastBeatPhase = beatPhase; }
    else { this._free += dt; if (this._free >= this._beatPeriod) { this._free -= this._beatPeriod; beatEdge = true; } }
    this._beats += dt / this._beatPeriod;
    const bp = beatPhase != null ? beatPhase : this._free / this._beatPeriod;
    const barPhase = director.bar != null ? director.bar : profile.barPhase;
    let barEdge = false;
    if (barPhase != null) { if (this._lastBarPhase != null && barPhase < this._lastBarPhase) barEdge = true; this._lastBarPhase = barPhase; }
    else if (beatEdge && this._beatCount % 4 === 0 && this._beatCount > 0) barEdge = true;
    const phrasePhase = director.phrase != null ? director.phrase : profile.phrasePhase;
    let phraseEdge = false;
    if (phrasePhase != null) { if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) phraseEdge = true; this._lastPhrasePhase = phrasePhase; }
    else if (beatEdge && this._beatCount % 32 === 0 && this._beatCount > 0) phraseEdge = true;
    if (beatEdge) { this._beatCount++; this._lastBeatAt = this._time; }
    if (barEdge) { this._barCount++; this._lastBarAt = this._time; }
    if (phraseEdge) this._phraseCount++;

    const ev = director.nextEvent;
    const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
    const beatsToDrop = dropMs != null && dropMs > 0 ? dropMs / 1000 / this._beatPeriod : null;
    const build = beatsToDrop != null && beatsToDrop < 32 ? clamp01((32 - beatsToDrop) / 28) : (director.mode === 'build' ? 0.4 : 0);
    this._build += (build - this._build) * Math.min(1, dt / 0.4);
    const nowMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    let drop = false;
    if (dropMs != null && dropMs <= dt * 500 + 30 && nowMs != null) {
      const key = Math.round((nowMs + dropMs) / 100);
      if (key !== this._lastDropKey) { this._lastDropKey = key; drop = true; }
    }

    // the scene: the cover within 2 s, the title at 8 s
    if (this._clock.wrapped) { this._titleDone = false; this._setTarget('cover', this._pickFront(), this._time + 0.05, 1.6, false); }
    if (!this._titleDone && this._clock.t >= 8 && this._clock.t < 9 && this._content !== 'title') { this._titleDone = true; this._setTarget('title', this._pickFront(), this._time + 0.05, this._beatPeriod * 2, false); }

    // the VJ
    const bar = this._beatPeriod * 4;
    if (drop && this._time - this._dropAt > bar) {
      this._dropAt = this._time; this._build = 0;
      this._setTarget('cover', 'center', this._time + 0.02, 0.0, false);               // everything at once, no flash (R9 under 0.80)
      this._say('DROP');
    } else if (barEdge) {
      if (this._build > 0.5) this._setTarget(this._pickContent(), this._pickFront(), this._time + 0.02, bar * (0.6 - 0.35 * this._build), this._build > 0.85);
      else if (this._barCount % 2 === 0 && this._time - this._dropAt > bar * 0.9 && !(this._clock.t < 2.5) && this._time >= (this._holdUntil || 0)) this._setTarget(this._pickContent(), this._pickFront(), this._time + 0.02, bar, false);
    }
    if (phraseEdge) { this._family = (this._family + 1) % 3; this._pal = (this._pal + 1) % 5; this._say('phrase: family ' + this._family); }
    if (beatEdge) { this._waveRow = (this._waveRow + 7) % this._rows; this._wave(this._waveRow); }

    // the uniforms
    this._simU.uMad.value = this._build > 0.85 ? 1 : 0;
    this._u.uGlyphScale.value = 1.0;
    this._post.update(vdjBeatChroma(bp, this._beatPeriod) * 0.5 + 0.5);
    this._u.uFlash.value = this._flash; this._flash = 0;
    // comfort: the shown grid's mean luminance (palette) over the housing
    let est = 0.05;
    if (this._lastGrid) { let s = 0; for (let i = 0; i < this._lastGrid.length; i += 7) { const idx = this._lastGrid[i]; if (idx < N_COLORS) { const c = this._palette[idx]; s += c.r * 0.2126 + c.g * 0.7152 + c.b * 0.0722; } else s += 0.12; } est = 0.05 + 0.75 * s / Math.ceil(this._lastGrid.length / 7); }
    this._lumSince += dt;
    if (this._lumSince > 0.25) { this._u.uGain.value = this._comfort.measure(est, this._lumSince); this._lumSince = 0; }
  }
  _pickFront() { return this._forcedFront || FRONTS[Math.floor(this._rng() * FRONTS.length)]; }
  _pickContent() {
    if (this._forcedContent) return this._forcedContent;
    const fam = [['cover', 'pattern', 'cover', 'text'], ['title', 'pattern', 'cover', 'pattern'], ['clock', 'cover', 'pattern', 'title']][this._family];
    return fam[this._barCount % fam.length];
  }

  _render(renderer) {
    renderer.getDrawingBufferSize(this._size);
    const prev = renderer.getRenderTarget();
    let W = this._size.x, H = this._size.y;
    if (prev) { W = prev.width; H = prev.height; }
    this._u.uScreen.value.set(W, H);
    const aspect = W / Math.max(1, H);
    if (Math.abs(aspect - this._aspect) > 1e-3) { this._aspect = aspect; this._u.uAspect.value = aspect; }
    // the glyph atlas, once the font is here (vector glyphs into 128 px cells, MSAA)
    if (this._font && !this._atlasReady) {
      this._atlasReady = true;
      renderer.setRenderTarget(this._atlas);
      renderer.setClearColor(0x000000, 1); renderer.clear(true, false, false);
      const meshes = [];
      for (let i = 0; i < GLYPHS.length && i < ATLAS_GRID * ATLAS_GRID; i++) {
        const ch = GLYPHS[i];
        if (ch === ' ') continue;
        const shapes = this._font.generateShapes(ch, 1);
        if (!shapes.length) continue;
        const g = new THREE.ShapeGeometry(shapes, 8);
        g.computeBoundingBox();
        const bb = g.boundingBox, w = bb.max.x - bb.min.x, h = bb.max.y - bb.min.y;
        const capH = (this._font.data.capHeight || 700) / this._font.data.resolution;
        // the glyph centred in its cell, cap height = 70 % of the cell, baseline from the cap
        const s = 0.7 / capH, cx = (i % ATLAS_GRID + 0.5) / ATLAS_GRID * 2 - 1, cy = (Math.floor(i / ATLAS_GRID) + 0.5) / ATLAS_GRID * 2 - 1;
        const cellW = 2 / ATLAS_GRID;
        const m = new THREE.Mesh(g, this._glyphMat.clone());
        m.material.uniforms.uPlace.value.set(cx - (bb.min.x + w / 2) * s * cellW, cy - (capH / 2) * s * cellW, s * cellW, s * cellW);
        m.frustumCulled = false;
        this._glyphScene.add(m); meshes.push(m);
      }
      renderer.render(this._glyphScene, this._ortho);
      for (const m of meshes) { this._glyphScene.remove(m); m.geometry.dispose(); m.material.dispose(); }
      renderer.setRenderTarget(prev);
    }
    // the state: initialised from the target once, then one simulation step per frame
    if (this._stateInit) {
      this._stateInit = false;
      // seed both states: current = next = target card, nothing flipping (the cover appears by its first front)
      this._simTime = -1;
      for (const st of [this._stateA, this._stateB]) {
        renderer.setRenderTarget(st); renderer.setClearColor(0x000000, 1); renderer.clear(true, false, false);
      }
    }
    // the simulation catches up from the last rendered instant in sub-steps of at most 1/60 s
    // (a skipped render, a hidden tab or a probe stepping without rendering lose no flips)
    if (this._simTime == null) this._simTime = this._time;
    let left = this._time - this._simTime, n = 0;
    if (left > 20) { this._simTime = this._time - 20; left = 20; }
    do {
      const step = Math.min(left, 1 / 60);
      this._simTime += step; left -= step;
      this._simU.uDt.value = step; this._simU.uTime.value = this._simTime;
      const read = this._stateA, write = this._stateB;
      this._simU.uState.value = read.texture;
      renderer.setRenderTarget(write);
      renderer.render(this._passScene, this._ortho);
      this._stateA = write; this._stateB = read;
    } while (left > 1e-6 && ++n < 1200);
    this._u.uState.value = this._stateA.texture;
    renderer.setRenderTarget(prev);
  }

  get probe() {
    return {
      tier: this._tier, grid: [this._cols, this._rows], content: this._content, front: this._front, family: this._family, build: +this._build.toFixed(2), bars: this._barCount,
      hasCover: !!this._coverGrid, palette: this._palette ? this._palette.slice(0, 4).map((c) => c.getHexString()) : null, font: !!this._font, atlas: this._atlasReady,
      title: this._title, decisions: this._decisions.slice(-8), gain: +this._u.uGain.value.toFixed(2), scene: +this._clock.t.toFixed(2)
    };
  }

  dispose() {
    if (this._mesh) { this._mesh.onBeforeRender = null; if (this._scene) this._scene.remove(this._mesh); this._mesh.geometry.dispose(); }
    for (const m of [this._mat, this._simMat, this._glyphMat]) if (m) m.dispose();
    if (this._passQuad) this._passQuad.geometry.dispose();
    for (const t of [this._stateA, this._stateB, this._atlas]) if (t) t.dispose();
    if (this._targetTex) this._targetTex.dispose();
    this._dropCover();
    this._scene = null;
  }
}
